import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Trash2, ShieldCheck, CheckCircle2, Circle, StickyNote } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { AdminNotes } from "@/components/AdminNotes";
import { MemberTimelineSheet } from "@/components/AdminTimeline";
import { TestAccountSweep } from "@/components/TestAccountSweep";
import { CURRICULUM } from "@/lib/curriculum";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MOD = Object.fromEntries(CURRICULUM.map((m) => [m.id, m]));
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return iso; } };
const ago = (iso) => {
  if (!iso) return "—";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return days === 0 ? "Today" : days === 1 ? "Yesterday" : days < 30 ? `${days}d ago` : fmt(iso);
};

const FILTERS = [["all", "All"], ["confirmed", "Confirmed"], ["unconfirmed", "Unconfirmed"], ["paid", "Paid"], ["graduates", "Graduates"], ["inactive", "Inactive 14 days"]];

export const AdminMembers = ({ onChanged }) => {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(null);
  const [openMember, setOpenMember] = useState(null);

  const load = (query = q, f = filter) => axios.get(`${API}/admin/users`, { params: { q: query, limit: 200, filter: f } }).then((r) => setData(r.data)).catch(() => setData({ users: [], total: 0 }));
  useEffect(() => { const t = setTimeout(() => load(q, filter), 250); return () => clearTimeout(t); }, [q, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (u) => {
    setBusy(u.id);
    try {
      await axios.delete(`${API}/admin/users/${u.id}`);
      toast.success(`Removed ${u.email}.`);
      setConfirmId(null);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove that account.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-members-panel">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <label className="flex items-center gap-3 border border-[#E7DFD5] px-4 py-3 w-full md:w-96 focus-within:border-[#F59E0B]">
          <Search size={14} className="text-[#78716C]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name…" data-testid="members-search"
                 className="bg-transparent flex-1 text-sm focus:outline-none placeholder:text-[#555]" />
        </label>
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716C]" data-testid="members-count">{data?.total ?? "—"} accounts</div>
      </div>
      <TestAccountSweep onSwept={() => { load(); onChanged?.(); }} />
      <div className="flex flex-wrap gap-2" data-testid="members-filters">
        {FILTERS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            data-testid={`members-filter-${key}`}
            aria-pressed={filter === key}
            className={`px-4 py-2 rounded-full text-[10px] font-mono uppercase tracking-[0.2em] border transition-colors ${filter === key ? "bg-[#F59E0B] text-black border-[#F59E0B]" : "border-[#E7DFD5] text-[#44403C] hover:border-[#F59E0B]"}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="tacc-card overflow-x-auto" data-testid="admin-members-table">
        <table className="w-full text-sm min-w-[1240px]">
          <thead>
            <tr className="text-left font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716C] border-b border-[#E7DFD5]">
              <th className="p-4">Joined</th><th className="p-4">Last login</th><th className="p-4">Member</th><th className="p-4">Confirmed</th><th className="p-4">Purchases</th><th className="p-4">Progress</th><th className="p-4">Interested in</th><th className="p-4">Streak</th><th className="p-4">Notes</th><th className="p-4 text-right">Remove</th>
            </tr>
          </thead>
          <tbody>
            {(data?.users || []).map((u) => (
              <tr key={u.id} className="border-b border-[#E7DFD5] hover:bg-black/5" data-testid={`member-row-${u.id}`}>
                <td className="p-4 text-[#78716C] whitespace-nowrap">{fmt(u.created_at)}</td>
                <td className="p-4 text-[#78716C] whitespace-nowrap" data-testid={`member-last-login-${u.id}`} title={u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "No login recorded yet"}>{ago(u.last_login_at)}</td>
                <td className="p-4">
                  <button onClick={() => setOpenMember(u)} className="text-left text-[#1C1917] underline decoration-[#F59E0B]/40 underline-offset-4 hover:text-[#B45309] transition-colors" title="Open history" data-testid={`member-open-${u.id}`}>
                    {u.name || "—"}
                  </button>
                  {u.role === "admin" && <ShieldCheck size={12} className="inline text-[#B45309] ml-1" title="Admin" />}
                  <div className="text-xs text-[#78716C]">{u.email} · {u.auth}</div>
                </td>
                <td className="p-4">{u.email_verified ? <CheckCircle2 size={15} className="text-emerald-400" /> : <Circle size={15} className="text-[#555]" />}</td>
                <td className="p-4 text-xs text-[#44403C]">{u.entitlements.filter((e) => e !== "modules").join(", ") || <span className="text-[#555]">none</span>}</td>
                <td className="p-4 text-xs text-[#44403C]">{u.graduated ? <span className="text-emerald-400 font-mono text-[10px] tracking-[0.2em] uppercase">Graduate</span> : `${u.completed_count} / 8`}</td>
                <td className="p-4">
                  {u.interested_in?.length ? (
                    <div className="flex flex-wrap gap-1.5 max-w-[240px]" data-testid={`member-interested-${u.id}`}>
                      {u.interested_in.slice(0, 4).map((it) => {
                        const m = MOD[it.id];
                        return (
                          <span key={it.id} title={`${m ? m.title : it.id} — previewed ${it.count}×`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#F59E0B]/40 text-[#B45309] text-[10px] font-mono">
                            {m ? m.n : it.id}{it.count > 1 && <span className="text-[#78716C]">×{it.count}</span>}
                          </span>
                        );
                      })}
                      {u.interested_in.length > 4 && <span className="text-[10px] text-[#78716C] font-mono self-center">+{u.interested_in.length - 4}</span>}
                    </div>
                  ) : <span className="text-[#555]">—</span>}
                </td>
                <td className="p-4 text-[#B45309]">{u.streak_current}</td>
                <td className="p-4">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        className={`inline-flex items-center gap-1.5 px-2 py-1 border text-xs transition-colors ${u.notes_count ? "border-[#F59E0B]/50 text-[#B45309]" : "border-[#E7DFD5] text-[#555] hover:text-[#B45309] hover:border-[#F59E0B]/50"}`}
                        title={u.last_note ? u.last_note.text : "Add a private note"}
                        data-testid={`member-notes-${u.id}`}
                      >
                        <StickyNote size={13} /> <span data-testid={`member-notes-count-${u.id}`}>{u.notes_count || 0}</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-96 bg-[#FFFFFF] border-[#F59E0B]/40 text-[#1C1917] p-4">
                      <div className="text-xs text-[#78716C] mb-3 truncate">{u.name || u.email}</div>
                      <AdminNotes targetType="member" targetId={u.id} onChanged={() => load()} testIdPrefix={`member-notes-${u.id}`} />
                    </PopoverContent>
                  </Popover>
                </td>
                <td className="p-4 text-right">
                  {u.role === "admin" ? (
                    <span className="text-[#555] font-mono text-[9px] tracking-[0.2em] uppercase">protected</span>
                  ) : confirmId === u.id ? (
                    <span className="inline-flex items-center gap-2" data-testid={`member-remove-confirm-${u.id}`}>
                      <span className="text-xs text-red-300">Remove {u.email}?</span>
                      <button onClick={() => remove(u)} disabled={busy === u.id} className="px-3 py-1.5 bg-red-500 text-black text-xs font-semibold disabled:opacity-50" data-testid={`member-remove-yes-${u.id}`}>Yes, remove</button>
                      <button onClick={() => setConfirmId(null)} className="px-3 py-1.5 border border-[#E7DFD5] text-xs" data-testid={`member-remove-no-${u.id}`}>Keep</button>
                    </span>
                  ) : (
                    <button onClick={() => setConfirmId(u.id)} className="p-2 text-[#555] hover:text-red-400 transition-colors" aria-label={`Remove ${u.email}`} data-testid={`member-remove-${u.id}`}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {data && data.users.length === 0 && (
              <tr><td colSpan={10} className="p-8 text-center text-[#78716C]">No accounts match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <MemberTimelineSheet member={openMember} onClose={() => setOpenMember(null)} />
    </div>
  );
};
